import { faWallet } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChevronsUpDown } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createHttpClient } from '@adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { WalletsApiAdapter } from '@modules/Wallets/List/adapters/WalletsApiAdapter';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { SidebarMenuButton, SidebarMenuItem, useSidebar } from '@shared/components/ui/sidebar';
import { UserAvatar } from '@shared/components/ui/UserAvatar';
import { usePermissions } from '@shared/hooks/usePermissions';
import { useUserData } from '@shared/hooks/useUser';
import { useAuthStore } from '@shared/stores/authStore';
import { useNavigation } from './useNavigation';
import { WorkspaceDropdownMenuItems } from './WorkspaceDropdownMenuItems';

type UserWithPhoto = {
  profile_photo_path?: string | null;
};

const formatCurrency = (value: number, currency?: string) => {
  if (Number.isNaN(value)) return 'N/D';

  try {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: currency || 'EUR',
      minimumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${value.toFixed(2)} ${currency || ''}`.trim();
  }
};

export const UserFooterItem = () => {
  const { t } = useTranslation('menu');
  const { isMobile } = useSidebar();
  const { logoDropdownItems } = useNavigation();
  const { getTeamType, hasPermission, isWalletVisible } = usePermissions();
  const authUser = useAuthStore((state) => state.user);
  const authTeam = useAuthStore((state) => state.team);
  const { data: userData } = useUserData();

  const user = userData?.user ?? authUser;
  const team = userData?.team ?? authTeam;
  const isExternal = getTeamType() === 'tenant_external';
  const showWallet = isExternal && isWalletVisible() && hasPermission('wallets.view');

  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const apiAdapter = useMemo(() => new WalletsApiAdapter(httpClient), [httpClient]);

  const { data: walletsData, isLoading: isLoadingWallet } = useQuery({
    queryKey: ['wallets', 'footer'],
    queryFn: () => apiAdapter.getWallets({}),
    enabled: showWallet,
    staleTime: 60 * 1000,
  });

  const wallet = useMemo(() => {
    if (!walletsData?.data || walletsData.data.length === 0) return null;
    return walletsData.data[0];
  }, [walletsData]);

  if (!user) {
    return null;
  }

  const profilePhoto = (user as UserWithPhoto).profile_photo_path ?? '';
  const formattedBalance =
    showWallet && wallet ? formatCurrency(wallet.balance_total ?? 0, wallet.currency ?? 'EUR') : null;

  return (
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <SidebarMenuButton
            size="lg"
            className="h-auto py-2 data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            data-testid="sidebar-user-footer"
          >
            <UserAvatar
              name={user.name}
              image={profilePhoto || undefined}
              size="sm"
              className="shrink-0"
              data-testid="sidebar-user-avatar"
            />

            <div className="min-w-0 flex-1 text-left group-data-[collapsible=icon]:hidden">
              <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
              {team?.name ? <p className="truncate text-xs text-muted-foreground">{team.name}</p> : null}
            </div>

            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50 group-data-[collapsible=icon]:hidden" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          side={isMobile ? 'bottom' : 'top'}
          align="start"
          sideOffset={8}
          className="w-screen max-w-[270px] sm:w-60"
        >
          <div className="px-2 py-1.5">
            <p className="truncate text-sm font-medium text-foreground">{user.name}</p>
            {team?.name ? <p className="truncate text-xs text-muted-foreground">{team.name}</p> : null}
          </div>

          <DropdownMenuSeparator />

          {showWallet ? (
            <DropdownMenuItem
              asChild
              className="bg-sidebar-accent/20 focus:bg-sidebar-accent/30 data-[highlighted]:bg-sidebar-accent/30"
            >
              <Link
                to="/wallets"
                className="flex w-full cursor-pointer items-center gap-2"
                data-testid="sidebar-user-wallet-balance"
              >
                <FontAwesomeIcon icon={faWallet} className="!h-4 !w-4 shrink-0" />
                <span className="truncate tracking-wide text-sidebar-accent-foreground/90">
                  {isLoadingWallet ? '...' : (formattedBalance ?? t('billeteras'))}
                </span>
              </Link>
            </DropdownMenuItem>
          ) : null}

          {showWallet && logoDropdownItems.length > 0 ? <DropdownMenuSeparator /> : null}

          <WorkspaceDropdownMenuItems items={logoDropdownItems} />
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  );
};
